import { useEffect, useState } from "react";
import { FileDown, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, errMsg, fmtNum, fmtDate, todayISO, downloadBlob } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { ScanStats } from "@/components/ScanStats";
import { useAuth } from "@/context/AuthContext";
import { AiReportInsight } from "@/components/AiPanels";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PERIODS = [["daily", "Harian"], ["weekly", "Mingguan"], ["monthly", "Bulanan"], ["yearly", "Tahunan"]];

export default function Reports() {
  const [period, setPeriod] = useState("daily");
  const { can } = useAuth(); const isAdmin = can("scan_stats");
  const [refDate, setRefDate] = useState(todayISO());
  const [rep, setRep] = useState(null);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    if (!refDate) return;
    api.get("/reports", { params: { period, ref_date: refDate } }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period, refDate]);

  const doExport = async (format) => {
    setExporting(format);
    try {
      const r = await api.get("/reports/export", { params: { period, ref_date: refDate, format }, responseType: "blob" });
      downloadBlob(r.data, `laporan_${period}_${rep.start}_${rep.end}.${format}`);
      toast.success(`Laporan ${format.toUpperCase()} berhasil diunduh`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(""); }
  };

  const label = PERIODS.find((p) => p[0] === period)[1];

  return (
    <div data-testid="reports-page">
      <PageHeader title="Laporan & Analisis" subtitle="Rekap pergerakan stok harian, mingguan, bulanan, dan tahunan">
        <Button variant="outline" className="rounded-full border-sky-300 text-sky-800" disabled={!rep || !!exporting} onClick={() => doExport("csv")} data-testid="btn-export-csv"><FileDown className="h-4 w-4 mr-1" /> {exporting === "csv" ? "Mengunduh..." : "Export CSV"}</Button>
        <Button className="rounded-full bg-sky-600 hover:bg-sky-700" disabled={!rep || !!exporting} onClick={() => doExport("pdf")} data-testid="btn-export-pdf"><FileText className="h-4 w-4 mr-1" /> {exporting === "pdf" ? "Mengunduh..." : "Export PDF"}</Button>
      </PageHeader>

      <div className="card-aqua p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3 rise rise-1">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList className="bg-sky-50">{PERIODS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`tab-period-${v}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <div className="flex items-center gap-2 md:ml-auto">
          <span className="text-sm text-slate-500">Tanggal acuan</span>
          <Input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} className="w-44" data-testid="input-report-date" />
        </div>
      </div>

      {can("ai_use") && <AiReportInsight key={`${period}-${refDate}`} period={period} refDate={refDate} />}

      {rep && (
        <>
          <div className="text-sm text-slate-500 mb-3 rise" data-testid="report-range">Laporan <b>{label}</b>: {fmtDate(rep.start)}{rep.start !== rep.end && ` – ${fmtDate(rep.end)}`}</div>
          <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
            {[["Total Masuk", rep.summary.total_in_mc, "text-emerald-600", "report-total-in"], ["Total Keluar", rep.summary.total_out_mc, "text-amber-600", "report-total-out"],
              ["Net Pergerakan", rep.summary.net_mc, rep.summary.net_mc >= 0 ? "text-sky-700" : "text-rose-600", "report-net"], ["Jumlah Transaksi", rep.summary.tx_count, "text-slate-900", "report-tx-count"],
              ["Stok Saat Ini", rep.summary.current_stock_mc, "text-slate-900", "report-current-stock"]].map(([t, v, c, id], i) => (
              <div key={t} className={`card-aqua p-4 rise rise-${(i % 4) + 1}`} data-testid={id}>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t}</div>
                <div className={`text-2xl font-extrabold font-mono mt-1 ${c}`}>{fmtNum(v)} <span className="text-xs text-slate-400 font-sans">{t === "Jumlah Transaksi" ? "tx" : "MC"}</span></div>
              </div>
            ))}
          </div>

          <div className="card-aqua p-5 mb-4 rise rise-2" data-testid="report-chart">
            <h3 className="font-semibold text-slate-800 mb-1">Pergerakan Stok {label}</h3>
            <p className="text-xs text-slate-500 mb-3">{period === "daily" ? "per jenis ikan" : period === "yearly" ? "per bulan" : "per hari"} • satuan MC</p>
            {rep.series.length === 0 ? <div className="text-center text-slate-500 py-12">Tidak ada transaksi pada periode ini.</div> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={rep.series}>
                  <CartesianGrid vertical={false} stroke="#E0F2FE" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} interval={period === "monthly" ? 2 : 0} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#F0F9FF" }} contentStyle={{ borderRadius: 12, border: "1px solid #BAE6FD" }} />
                  <Legend iconType="circle" />
                  <Bar dataKey="masuk" name="Masuk" fill="#0284C7" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="keluar" name="Keluar" fill="#7DD3FC" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
            <div className="card-aqua overflow-hidden xl:col-span-2 rise rise-3">
              <div className="px-5 pt-4 pb-2 font-semibold text-slate-800">Rekap per Jenis & Size</div>
              <table className="w-full text-sm" data-testid="report-species-table">
                <thead className="table-head"><tr><th className="text-left px-4 py-2">Jenis</th><th className="text-left px-4 py-2">Size</th><th className="text-right px-4 py-2">Masuk</th><th className="text-right px-4 py-2">Keluar</th><th className="text-right px-4 py-2">Net</th></tr></thead>
                <tbody>
                  {rep.by_species.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">Tidak ada data</td></tr>}
                  {rep.by_species.map((r, i) => (
                    <tr key={i} className="border-t border-sky-50">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{r.species_name}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.size}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{fmtNum(r.masuk)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-amber-600">{fmtNum(r.keluar)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold">{fmtNum(r.masuk - r.keluar)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-aqua overflow-hidden xl:col-span-3 rise rise-4">
              <div className="px-5 pt-4 pb-2 font-semibold text-slate-800">Detail Transaksi ({rep.transactions.length})</div>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="report-tx-table">
                  <thead className="table-head sticky top-0"><tr><th className="text-left px-4 py-2">Tanggal</th><th className="text-left px-4 py-2">Tipe</th><th className="text-left px-4 py-2">Batch</th><th className="text-left px-4 py-2">Ikan / Size</th><th className="text-left px-4 py-2">Asal</th><th className="text-right px-4 py-2">MC</th><th className="text-left px-4 py-2">Operator</th></tr></thead>
                  <tbody>
                    {rep.transactions.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">Tidak ada transaksi</td></tr>}
                    {rep.transactions.map((t) => (
                      <tr key={t.id} className="border-t border-sky-50">
                        <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(t.date)}</td>
                        <td className={`px-4 py-2.5 font-semibold ${t.type === "in" ? "text-emerald-600" : "text-amber-600"}`}>{t.type === "in" ? "Masuk" : "Keluar"}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-sky-800">{t.batch_code}</td>
                        <td className="px-4 py-2.5 text-slate-800">{t.species_name} <span className="text-slate-400">• {t.size}</span></td>
                        <td className="px-4 py-2.5 text-slate-600">{t.origin_name}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold">{t.type === "in" ? "+" : "-"}{fmtNum(t.quantity_mc)} <span className="text-[10px] text-slate-400">{t.unit || "MC"}</span></td>
                        <td className="px-4 py-2.5 text-slate-600">{t.user_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {isAdmin && <ScanStats />}
    </div>
  );
}
